/**
 * 关键词变化组件
 * 设计稿要求：
 * - 按钮顺序：表格 → 水平条形图 → 折线图 → 词云图
 * - 默认显示水平条形图
 * - 左侧关键词名称，中间蓝色条形，右侧"声量 (环比变化率%)"
 * - 语言选择器
 */
import React, { useCallback, useContext, useMemo, useState } from 'react';
import CardCommon from '@op/components/CardCommon';
import {
  tableIcon,
  lineIcon,
  cloudIcon,
  commonBarHIcon,
  SymbolType,
} from '@op/constants/chart';
import useLanguage from '@op/hooks/useLanguage';
import { DBO_U, DBO_V } from '@op/utils/format';
import { languageSelectOptions } from '@op/components/FilterBoxCommon/constant';
import { useKeywordData } from '../../../hooks/useKeywordData';
import BarChart from './BarChart';
import LineChart from './LineChart';
import WordCloudChart from './WordCloudChart';
import TableChart from './TableChart';
import './index.scss';
import { SecurityWeeklyReportContext } from '@op/pages/SecurityWeeklyReport/context';
export interface ISelectedSubTopic {
  name: string;
  category: string;
  sub_category: string;
}
interface IKeywordAnalysisProps {
  filter: any;
  selectedSubTopic: null | ISelectedSubTopic;
}

const KeywordAnalysis: React.FC<IKeywordAnalysisProps> = ({
  filter,
  selectedSubTopic,
}) => {
  const { toFeeds } = useContext(SecurityWeeklyReportContext);
  const { formatMessage, formatOptionsMessage, isEn } = useLanguage();
  const [chartType, setChartType] = useState<SymbolType>('common-bar'); // 默认水平条形图
  const [language, setLanguage] = useState<string>('en');

  const {
    listData,
    trendData,
    loading,
    empty,
    topFluctuation,
    newKeywords,
    keywordMap,
  } = useKeywordData(filter);

  // 获取关键词显示名称（接收原始 keyword 字符串）
  const getKeywordName = useCallback(
    (originKeyword: string) => {
      const item = keywordMap[originKeyword];
      if (!item) return originKeyword;
      if (language === 'zh' && item.keyword_cn) return item.keyword_cn;
      if (language === 'en' && item.keyword_en) return item.keyword_en;
      return item.keyword;
    },
    [language, keywordMap],
  );

  // 下载处理
  const handleDownload = () => {
    if (!trendData?.tableRecord) return;
    const name = formatMessage({ id: 'Keywords Changes' });
    DBO_U.download({
      key: 'WeeklyKeywords',
      fileName: name,
      filter,
      data: trendData.tableRecord,
      auxiliaryParams: {
        filter,
        columns: trendData.legend,
        keywordMap,
        language,
        isEn,
      },
      needFilterTime: true,
    });
  };

  // 格式化关键词项：{关键词名}(Change {比率})
  const formatKeywordItem = useCallback(
    (item: { keyword: string; change_ratio: number }) => {
      const name = getKeywordName(item.keyword);
      const changeLabel = formatMessage({ id: 'change' });
      const ratio = DBO_V.ratio(item.change_ratio, '-', 2);
      return `${name}(${changeLabel} ${ratio})`;
    },
    [getKeywordName, formatMessage],
  );

  // 波动贡献率展示
  const newKeywordsText = useMemo(() => {
    // 过滤掉 change_ratio 为 0 的项，取 TOP3
    const topFluctuationArr = (topFluctuation || [])
      .filter((item) => item.change_ratio !== 0)
      .slice(0, 3);

    // 构建波动 TOP3 字符串
    const topFluctuationStr = topFluctuationArr
      .map(formatKeywordItem)
      .join(', ');

    // 过滤掉 change_ratio 为 0 的项，取 TOP2
    const suddenKeywordsArr = (newKeywords || [])
      .filter((item) => item.change_ratio !== 0)
      .slice(0, 2);

    // 构建突增 TOP2 字符串
    const suddenKeywordsStr = suddenKeywordsArr
      .map(formatKeywordItem)
      .join(', ');

    // 如果没有有效的波动数据，不显示
    const topFluctuationText =
      topFluctuationArr.length === 0
        ? ''
        : formatMessage(
            { id: "Top contributors to this week's fluctuation: {A};" },
            { A: topFluctuationStr },
          );

    // 如果没有有效的突增关键词，不显示
    const suddenKeywordText =
      suddenKeywordsArr.length === 0
        ? ''
        : formatMessage(
            { id: 'Trending Keywords: {A}' },
            { A: suddenKeywordsStr },
          );

    return [topFluctuationText, suddenKeywordText].filter(Boolean).join(' ');
  }, [topFluctuation, newKeywords, formatMessage, formatKeywordItem]);

  function gotoFeeds(keyword: string | null = null) {  
    if (keyword) {
      toFeeds(selectedSubTopic?.category, selectedSubTopic?.sub_category, {
        keyword: [keyword],
      });
      return;
    }
    toFeeds();
  }

  // 根据图表类型渲染对应组件
  const renderContent = () => {
    if (chartType === 'table') {
      return <TableChart data={trendData} getKeywordName={getKeywordName} />;
    }

    if (chartType === 'cloud') {
      return (
        <WordCloudChart
          height={416}
          data={listData}
          showFeedsFooter={true}
          onFeedsClick={gotoFeeds}
          getKeywordName={getKeywordName}
        />
      );
    }

    if (chartType === 'line') {
      return (
        <LineChart
          data={trendData}
          height={416}
          getKeywordName={getKeywordName}
          filter={filter}
          onFeedsClick={gotoFeeds}
        />
      );
    }

    // 默认：水平条形图
    return (
      <BarChart
        height={397}
        data={listData}
        getKeywordName={getKeywordName}
        filter={filter}
        onFeedsClick={gotoFeeds}
      />
    );
  };

  return (
    <CardCommon
      className="dbo-keyword-analysis"
      title={<span>{formatMessage({ id: 'Keywords Changes' })}</span>}
      subTitle={newKeywordsText}
      operations={[commonBarHIcon, cloudIcon, lineIcon, tableIcon]}
      defaultOperation="common-bar"
      handleOperationsOnClick={(item) => {
        setChartType((item?.symbol || 'common-bar') as 'table');
      }}
      loading={loading}
      empty={empty}
      loadingHeight={400}
      emptyHeight={400}
      alwaysShowOperations
      hasDownload={!empty}
      handleDownloadOnClick={handleDownload}
      logBtnId="WR_Keywords"
      dropdowns={formatOptionsMessage(languageSelectOptions)}
      defaultDropDown={language}
      dropdownFooter={
        <div style={{ textAlign: 'left', maxWidth: 204 }}>
          {formatMessage({
            id: 'From GPT-Translator. Powered by DataBrain NLP Team',
          })}
        </div>
      }
      handleDropdownOnChange={({ value }: { value: string }) => {
        setLanguage(value);
      }}
      contentCard
    >
      {renderContent()}
    </CardCommon>
  );
};

export default KeywordAnalysis;
